import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Brain, Calendar, Download, Users, DollarSign, LogOut, ShoppingBag, Plus, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI, bookingAPI, purchaseAPI } from '../services/api';

const SESSION_TYPES = [
  { label: '60-Minute Deep Dive', value: 'Individual Therapy Session', price: 1000 },
  { label: 'Follow-up Session', value: 'Follow-up Session', price: 1000 },
  { label: 'Discovery Call (20 min)', value: 'Discovery Call', price: 250 }
];

const TIME_SLOTS = [
  '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'
];

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  // Manual booking dialog state
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({
    clientName: '',
    email: '',
    phone: '',
    sessionType: SESSION_TYPES[0].value,
    date: '',
    time: TIME_SLOTS[0]
  });
  const [submittingManual, setSubmittingManual] = useState(false);

  const resetManualForm = () => setManualForm({
    clientName: '', email: '', phone: '',
    sessionType: SESSION_TYPES[0].value, date: '', time: TIME_SLOTS[0]
  });

  const handleCreateManualBooking = async (e) => {
    e.preventDefault();
    if (!manualForm.clientName || !manualForm.email || !manualForm.date || !manualForm.time) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmittingManual(true);
    try {
      const svc = SESSION_TYPES.find(s => s.value === manualForm.sessionType);
      const response = await bookingAPI.createManual({
        clientName: manualForm.clientName,
        email: manualForm.email,
        phone: manualForm.phone || 'N/A',
        service: svc.value,
        servicePrice: svc.price,
        date: manualForm.date,
        time: manualForm.time,
        message: 'Manually created by practitioner'
      }, token);
      if (response.success) {
        toast.success('Manual booking created. Slot blocked. Confirmation email sent.');
        setShowManualDialog(false);
        resetManualForm();
        fetchData(token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create manual booking');
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleMarkComplete = async (bookingId, service) => {
    try {
      const response = await bookingAPI.markComplete(bookingId, token);
      if (response.success) {
        const isDiscovery = service === 'Discovery Call';
        toast.success(
          isDiscovery
            ? 'Marked complete. Follow-up email scheduled in 2 hours.'
            : 'Booking marked as completed.'
        );
        fetchData(token);
      }
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (savedToken) => {
    try {
      await authAPI.verify(savedToken);
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchData(savedToken);
    } catch (error) {
      localStorage.removeItem('adminToken');
    }
  };

  const fetchData = async (authToken) => {
    setLoading(true);
    try {
      const [bookingsRes, purchasesRes] = await Promise.all([
        bookingAPI.getAll(authToken),
        purchaseAPI.getAll(authToken)
      ]);
      
      if (bookingsRes.success) setBookings(bookingsRes.bookings);
      if (purchasesRes.success) setPurchases(purchasesRes.purchases);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await authAPI.login(email, password);
      
      if (response.success) {
        localStorage.setItem('adminToken', response.token);
        setToken(response.token);
        setIsLoggedIn(true);
        toast.success(`Welcome back, ${response.admin.name}!`);
        fetchData(response.token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setEmail('');
    setPassword('');
    setToken('');
    setBookings([]);
    setPurchases([]);
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'pending':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/amcb4t0p_Generated_image%20%281%29.png"
                alt="Therapy with Ayesha"
                className="h-24 w-auto"
              />
            </div>
            <div>
              <CardTitle className="text-2xl font-serif">Admin Login</CardTitle>
              <CardDescription>Access your practice dashboard</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="admin-login-button"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalRevenue = bookings
    .filter(b => b.paymentStatus === 'completed')
    .reduce((sum, b) => sum + (b.servicePrice || 0), 0)
    + purchases
    .filter(p => p.paymentStatus === 'completed')
    .reduce((sum, p) => sum + (p.resourcePrice || 0), 0);

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-20 items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/amcb4t0p_Generated_image%20%281%29.png"
              alt="Therapy with Ayesha"
              className="h-14 w-auto"
            />
            <span className="text-lg font-serif font-semibold text-muted-foreground border-l pl-3">Admin Dashboard</span>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Stats Overview */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Bookings</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" data-testid="total-bookings-stat">{bookings.length}</div>
                <p className="text-xs text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Resource Purchases</CardTitle>
                <ShoppingBag className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" data-testid="total-purchases-stat">{purchases.length}</div>
                <p className="text-xs text-muted-foreground mt-1">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" data-testid="total-revenue-stat">
                  ₹{totalRevenue.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Confirmed payments</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending Bookings</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" data-testid="pending-bookings-stat">
                  {bookings.filter(b => b.status === 'pending').length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Awaiting payment</p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Tabs defaultValue="bookings" className="space-y-6">
            <TabsList>
              <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings ({bookings.length})</TabsTrigger>
              <TabsTrigger value="purchases" data-testid="purchases-tab">Purchases ({purchases.length})</TabsTrigger>
              <TabsTrigger value="forms" data-testid="forms-tab">Intake Forms</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-2xl font-serif">Recent Bookings</CardTitle>
                    <CardDescription>Manage your therapy sessions and appointments</CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowManualDialog(true)}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="create-manual-booking-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Booking for Client
                  </Button>
                </CardHeader>
                <CardContent>
                  {bookings.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No bookings yet
                    </div>
                  ) : (
                    <Table data-testid="bookings-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client Name</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Date & Time</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking._id}>
                            <TableCell className="font-medium">{booking.clientName}</TableCell>
                            <TableCell>{booking.service}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{booking.date ? new Date(booking.date).toLocaleDateString('en-IN') : '—'}</div>
                                <div className="text-muted-foreground">{booking.time || '—'}</div>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold">₹{booking.servicePrice}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(booking.status)}>
                                {booking.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(booking.paymentStatus)}>
                                {booking.paymentStatus}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs space-y-1">
                                <div>{booking.email}</div>
                                <div className="text-muted-foreground">{booking.phone}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkComplete(booking._id, booking.service)}
                                  data-testid={`mark-complete-${booking._id}`}
                                  title={booking.service === 'Discovery Call' ? 'Marks complete and schedules 2h follow-up email' : 'Marks session as completed'}
                                >
                                  <CheckCheck className="w-3.5 h-3.5 mr-1" />
                                  Complete
                                </Button>
                              )}
                              {booking.status === 'completed' && (
                                <span className="text-xs text-emerald-700 font-medium">✓ Done</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="purchases" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-serif">Resource Purchases</CardTitle>
                  <CardDescription>Track digital resource sales</CardDescription>
                </CardHeader>
                <CardContent>
                  {purchases.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No purchases yet
                    </div>
                  ) : (
                    <Table data-testid="purchases-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Buyer</TableHead>
                          <TableHead>Resource</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Payment Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {purchases.map((purchase) => (
                          <TableRow key={purchase._id}>
                            <TableCell>
                              <div className="text-sm">
                                <div className="font-medium">{purchase.buyerName}</div>
                                <div className="text-muted-foreground text-xs">{purchase.buyerEmail}</div>
                              </div>
                            </TableCell>
                            <TableCell>{purchase.resourceTitle}</TableCell>
                            <TableCell className="font-semibold">₹{purchase.resourcePrice}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getStatusColor(purchase.paymentStatus)}>
                                {purchase.paymentStatus}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(purchase.createdAt).toLocaleDateString('en-IN')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="forms" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl font-serif">Client Intake Forms</CardTitle>
                  <CardDescription>View and manage client intake submissions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 space-y-4">
                    <Download className="w-12 h-12 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">
                      Form submissions are collected via Google Forms.
                    </p>
                    <Button variant="outline" asChild>
                      <a 
                        href="https://docs.google.com/forms/d/1WJclltaZX4in4LPxoVnRqn12MiEucZ4fMHu_U8kjI00/edit#responses" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        View Form Responses
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Manual Booking Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Create Booking for Client</DialogTitle>
            <DialogDescription>
              Bypass payment gateway and block this slot. Confirmation email sent automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateManualBooking} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="m-name">Client Name *</Label>
              <Input
                id="m-name"
                data-testid="manual-client-name"
                value={manualForm.clientName}
                onChange={(e) => setManualForm({ ...manualForm, clientName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-email">Client Email *</Label>
              <Input
                id="m-email"
                type="email"
                data-testid="manual-client-email"
                value={manualForm.email}
                onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-phone">Phone (optional)</Label>
              <Input
                id="m-phone"
                value={manualForm.phone}
                onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-type">Session Type *</Label>
              <Select
                value={manualForm.sessionType}
                onValueChange={(v) => setManualForm({ ...manualForm, sessionType: v })}
              >
                <SelectTrigger data-testid="manual-session-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SESSION_TYPES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="m-date">Date *</Label>
                <Input
                  id="m-date"
                  type="date"
                  data-testid="manual-date"
                  value={manualForm.date}
                  onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-time">Time Slot *</Label>
                <Select
                  value={manualForm.time}
                  onValueChange={(v) => setManualForm({ ...manualForm, time: v })}
                >
                  <SelectTrigger data-testid="manual-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setShowManualDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingManual}
                className="bg-primary hover:bg-primary/90"
                data-testid="submit-manual-booking"
              >
                {submittingManual ? 'Creating…' : 'Create & Block Slot'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
